import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { RootState } from '../../../../store/rootState'
import { IUserDetail } from '../../../../types/user'
import * as LoginActions from '../../../login/actions/authActions'
import * as DepartmentActions from '../../../settings/actions/departmentActions'
import * as EmployeesActions from '../../../settings/actions/employeeActions'
import { IDepartment } from '../../../settings/types/department'
import { IEmployee } from '../../../settings/types/employee'
import * as ActsActions from '../../actions/activitiesActions'
import * as CostsActions from '../../actions/directAndPassTroughCosts'
import * as ProjectActions from '../../actions/projectActions'
import * as ProjectsActions from '../../actions/projectsActions'
import * as WizardActions from '../../actions/wizardActions'
import BudgetImportWizard from '../../budget-import/BudgetImportWizard'
import WizardChooseProjectType from '../../components/wizard/WizardChooseProjectType'
import WizardEnterActivities from '../../components/wizard/WizardEnterActivities'
import WizardEnterBaseData from '../../components/wizard/WizardEnterBaseData'
import WizardEnterCosts from '../../components/wizard/WizardEnterCosts'
import WizardFinishStep from '../../components/wizard/WizardFinishStep'
import WizardSelectBillingProcedure from '../../components/wizard/WizardSelectBillingProcedure'
import WizardSelectPhase from '../../components/wizard/WizardSelectPhase'
import WizardSelectTeam from '../../components/wizard/WizardSelectTeam'
import { getProjectWizardDataSelector } from '../../selectors/wizardSelector'
import { IDirectCost, IPassThroughCost } from '../../types/directAndPassTroughCosts'
import { ICustomer, IProject, IProjectFilters } from '../../types/project'
import { IProjectActivity } from '../../types/projectActivity'
import { IWizardProject } from '../../types/wizardData'

interface StateProps {
  wizardStep: string
  wizardData: IWizardProject
  wizardFinishedSteps: number[]
  departments: IDepartment[]
  employees: IEmployee[]
  user: IUserDetail | null
  filters: IProjectFilters
  activities: IProjectActivity[]
  directCosts: IDirectCost[]
  passTroughCosts: IPassThroughCost[]
  customers: ICustomer[]
}

interface DispatchProps {
  setWizardStep: (step: string) => void
  setProjectWizardData: (data: IWizardProject) => void
  wizardCreateProject: (project: IWizardProject) => Promise<IProject>
  setWizardFinishedSteps: (steps: number[]) => void
  getDepartments: () => void
  getEmployees: () => void
  getProjects: (queryParams?: IProjectFilters) => void
  getActs: (projectId: number) => Promise<IProjectActivity[]>
  getDirectCosts: (projectId: number) => Promise<IDirectCost[]>
  getPassTroughCosts: (projectId: number) => Promise<IPassThroughCost[]>
  getCustomers: () => void
  getLoggedInUser: () => Promise<IUserDetail>
  getProject: (id: number) => Promise<IProject>
  createProjectSuccess: (project: IProject) => void
}

type Props = StateProps & DispatchProps

const ProjectCreateWizardContainer = (props: Props) => {
  const {
    wizardStep,
    wizardData,
    wizardFinishedSteps,
    departments,
    employees,
    user,
    filters,
    customers,
    activities,
    directCosts,
    passTroughCosts,
    setProjectWizardData,
    getDepartments,
    getActs,
    getDirectCosts,
    getPassTroughCosts,
    getEmployees,
    setWizardStep,
    setWizardFinishedSteps,
    wizardCreateProject,
    getProjects,
    getCustomers,
    getLoggedInUser,
    getProject,
    createProjectSuccess
  } = props

  useEffect(() => {
    getDepartments()
    getEmployees()
    getCustomers()
  }, [getDepartments, getEmployees, getCustomers])

  const onClose = () => {
    setTimeout(() =>  {
      setWizardStep('')
      setWizardFinishedSteps([])
    })
  }

  const wizardCreateProj = (project: IWizardProject) => {
    const {
      // eslint-disable-next-line
      projectClonedState,
      // eslint-disable-next-line
      clonedTasksList,
      ...rest
    } = project
    wizardCreateProject({...rest, accounting: false}).then(response => {
      getLoggedInUser().then(() => getProjects(filters))
      if (Object.keys(response).length !== 0) {
        if (wizardData.type === 'service_provider') {
          if (!props.wizardFinishedSteps.includes(6)) {
            props.setWizardFinishedSteps([1, 2, 3, 4, 5, 6])
          }
          setWizardStep('step-seven')
        }
        else {
          if (!props.wizardFinishedSteps.includes(5)) {
            props.setWizardFinishedSteps([1, 2, 3, 4, 5])
          }
          setWizardStep('step-six')
        }
      }
    })
  }

  const renderTasksOrBillingProcedureWizardStep = () => {
    if (wizardData.type === 'service_provider') {
      return (
        <WizardSelectBillingProcedure
          onClose={onClose}
          open={true}
          data={wizardData}
          wizardFinishedSteps={wizardFinishedSteps}
          setWizardFinishedSteps={setWizardFinishedSteps}
          setWizardStep={setWizardStep}
          setProjectWizardData={setProjectWizardData}
          user={user}
        />
      )
    } else {
      return (
        <WizardEnterActivities
          departments={departments}
          onClose={onClose}
          open={true}
          data={wizardData}
          wizardFinishedSteps={wizardFinishedSteps}
          user={user}
          activities={activities}
          setWizardFinishedSteps={setWizardFinishedSteps}
          setWizardStep={setWizardStep}
          setProjectWizardData={setProjectWizardData}
          wizardCreateProject={wizardCreateProj}
          getActs={getActs}
        />
      )
    }
  }

  const renderFinishOrCostsWizardStep = () => {
    if (wizardData.type === 'service_provider') {
      return (
        <WizardEnterCosts
          departments={departments}
          onClose={onClose}
          open={true}
          data={wizardData}
          wizardFinishedSteps={wizardFinishedSteps}
          user={user}
          directCosts={directCosts}
          passTroughCosts={passTroughCosts}
          setWizardFinishedSteps={setWizardFinishedSteps}
          setWizardStep={setWizardStep}
          setProjectWizardData={setProjectWizardData}
          wizardCreateProject={wizardCreateProj}
          getDirectCosts={getDirectCosts}
          getPassTroughCosts={getPassTroughCosts}
        />
      )
    } else {
      return (
        <WizardFinishStep
          onClose={onClose}
          open={true}
          data={wizardData}
          wizardFinishedSteps={wizardFinishedSteps}
          setWizardFinishedSteps={setWizardFinishedSteps}
          setWizardStep={setWizardStep}
          setProjectWizardData={setProjectWizardData}
        />
      )
    }
  }

  const renderModals = () => {
    switch (wizardStep) {
      case 'step-one':
        return (
          <WizardChooseProjectType
            onClose={onClose}
            open={true}
            data={wizardData}
            wizardFinishedSteps={wizardFinishedSteps}
            setWizardFinishedSteps={setWizardFinishedSteps}
            setWizardStep={setWizardStep}
            setProjectWizardData={setProjectWizardData}
            user={user}
          />
        )
      case 'step-two':
        return (
          <WizardEnterBaseData
            customers={customers}
            onClose={onClose}
            open={true}
            data={wizardData}
            wizardFinishedSteps={wizardFinishedSteps}
            setWizardFinishedSteps={setWizardFinishedSteps}
            setWizardStep={setWizardStep}
            setProjectWizardData={setProjectWizardData}
          />
        )
      case 'step-three':
        return (
          <WizardSelectPhase
            onClose={onClose}
            open={true}
            data={wizardData}
            wizardFinishedSteps={wizardFinishedSteps}
            setWizardFinishedSteps={setWizardFinishedSteps}
            setWizardStep={setWizardStep}
            setProjectWizardData={setProjectWizardData}
          />
        )
      case 'step-four':
        return (
          <WizardSelectTeam
            employees={employees}
            onClose={onClose}
            open={true}
            data={wizardData}
            wizardFinishedSteps={wizardFinishedSteps}
            setWizardFinishedSteps={setWizardFinishedSteps}
            setWizardStep={setWizardStep}
            setProjectWizardData={setProjectWizardData}
            user={user}
          />
        )
      case 'step-five':
        return renderTasksOrBillingProcedureWizardStep()
      case 'step-six':
        return renderFinishOrCostsWizardStep()
      case 'step-seven':
        return (
          <WizardFinishStep
            onClose={onClose}
            open={true}
            data={wizardData}
            wizardFinishedSteps={wizardFinishedSteps}
            setWizardFinishedSteps={setWizardFinishedSteps}
            setWizardStep={setWizardStep}
            setProjectWizardData={setProjectWizardData}
          />
        )
      case 'budget-selection':
      case 'budget-import-select-team':
      case 'budget-import-select-billing':
      case 'budget-import-finish':
        return (
          <BudgetImportWizard
            data={wizardData}
            setProjectWizardData={setProjectWizardData}
            employees={employees}
            user={user}
            onClose={onClose}
            getProjects={getProjects}
            getLoggedInUser={getLoggedInUser}
            getProject={getProject}
            createProjectSuccess={createProjectSuccess}
            filters={filters}
          />
        )

      default:
        return null
    }
  }

  return renderModals()
}

const mapStateToProps = (state: RootState): StateProps => {
  return {
    wizardStep: state.projectsState.wizardApp.wizardStep,
    wizardData: getProjectWizardDataSelector(state),
    wizardFinishedSteps: state.projectsState.wizardApp.wizardFinishedSteps,
    departments: state.userSettingsState.departments,
    employees: state.userSettingsState.employees,
    user: state.authState.user,
    filters: state.projectsState.filterProjects,
    activities: state.projectsState.activities,
    directCosts: state.projectsState.directCosts,
    passTroughCosts: state.projectsState.passTroughCosts,
    customers: state.projectsState.customers
  }
}

const mapDispatchToProps = (
  dispatch:
    WizardActions.WizadThunkDispatchType
    & DepartmentActions.DepartmentThunkDispatchType
    & EmployeesActions.EmployeeThunkDispatchType
    & ProjectsActions.ProjectsThunkDispatchType
    & ProjectActions.ProjectThunkDispatchType
    & ActsActions.ActivitiesThunkDispatchType
    & CostsActions.DirectAndPassTroughCostThunkDispatchType
    & LoginActions.AuthThunkDispatchType
  ): DispatchProps => {
  return {
    setWizardStep: (step: string) => dispatch(WizardActions.setWizardStep(step)),
    setProjectWizardData: (data: IWizardProject) => dispatch(WizardActions.setProjectWizardData(data)),
    wizardCreateProject: async project => dispatch(WizardActions.wizardCreateProject(project)),
    setWizardFinishedSteps: (steps: number[]) => dispatch(WizardActions.setWizardFinishedSteps(steps)),
    getDepartments: () => dispatch(DepartmentActions.getDepartments()),
    getEmployees: () => dispatch(EmployeesActions.getEmployees()),
    getProjects: filters => dispatch(ProjectsActions.getProjects(filters)),
    getActs: (projectId) => dispatch(ActsActions.getActivities(projectId)),
    getDirectCosts: (projectId) => dispatch(CostsActions.getDirectCosts(projectId)),
    getPassTroughCosts: (projectId) => dispatch(CostsActions.getPassTroughCosts(projectId)),
    getCustomers: () => dispatch(ProjectActions.getCustomers()),
    getLoggedInUser: () => dispatch(LoginActions.getLoggedInUser()),
    getProject: (id) => dispatch(ProjectActions.getProject(id)),
    createProjectSuccess: (project) => dispatch(ProjectActions.createProjectSuccess(project))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectCreateWizardContainer)
